import { useLayoutEffect, useRef, useState } from "react";

import { authClient } from "@/lib/auth-client";
import { getSupportActor } from "@/utils/supportSession";
import { stopLessonSession } from "@/features/tutor/utils/stopLessonSession";
import { useAuthStore } from "@/store/useAuthStore";

/**
 * Persistent marker that this browser is inside somebody else's account.
 *
 * A support session is byte-for-byte an ordinary session — same cookie, same
 * app, nothing about the UI differs — so without this bar an operator has no
 * way to tell whose account they are typing into. Deliberately not
 * dismissable, and rendered above the router so it shows on every route.
 *
 * `impersonatedBy` is stamped on the session by the support-login redeem
 * endpoint (and by better-auth's own impersonation route). Read straight from
 * the session rather than mirrored into the auth store: the store resets the
 * token in six places, and a flag that must stay in step with all of them is a
 * flag that eventually won't.
 */
export const SupportSessionBanner = () => {
  const { data } = authClient.useSession();
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const cachedUser = useAuthStore((state) => state.user);
  const [ending, setEnding] = useState(false);
  const [endError, setEndError] = useState<string | null>(null);
  const barRef = useRef<HTMLDivElement | null>(null);

  const sessionImpersonatedBy = (
    data?.session as { impersonatedBy?: string | null } | undefined
  )?.impersonatedBy;

  // Two sources, on purpose. The live session is authoritative, but if
  // useSession is slow or fails transiently, initialize() still restores the
  // cached target and syncSession deliberately keeps that authenticated state
  // -- so the app stays usable while this hook has nothing. The persisted
  // marker already proves support mode in exactly that window, and the whole
  // point of this banner is that protected UI must never be usable without it.
  // Fail towards showing the banner.
  const impersonatedBy = sessionImpersonatedBy || getSupportActor();

  // Publish this bar's height so full-viewport layouts can subtract it.
  //
  // `.fullscreen` (the lesson view) is `position: fixed; height: 100dvh` with
  // no `top`, so it starts at its *static* position -- which this bar pushes
  // down -- while still being a whole viewport tall. Its bottom strip, which
  // carries "Hold to speak" and "Exit Lesson", then falls below the fold.
  //
  // Measured rather than hard-coded: the bar is `flex-wrap` and takes a second
  // line on narrow viewports, so a constant would be wrong exactly when the
  // screen can least afford it.
  useLayoutEffect(() => {
    const el = barRef.current;
    const root = document.documentElement;
    if (!el) {
      root.style.removeProperty("--support-banner-h");
      return;
    }
    const publish = () =>
      root.style.setProperty("--support-banner-h", `${el.offsetHeight}px`);
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.removeProperty("--support-banner-h");
    };
  }, [impersonatedBy]);

  if (!impersonatedBy) return null;

  // Falls back to the cached store user when the hook has not resolved, so the
  // banner names somebody rather than "user ".
  const who =
    data?.user?.name ||
    data?.user?.email ||
    cachedUser?.name ||
    cachedUser?.email ||
    (cachedUser?.id ? `user ${cachedUser.id}` : "this student");

  // signOut, not stopImpersonating: there is no operator session to restore in
  // this browser. The link was opened here, so the only session that exists is
  // the support one, and better-auth's stop route looks for an `admin_session`
  // cookie it will never find.
  //
  // Called directly rather than through clearAuth, which swallows a signOut
  // failure. If the auth service is down, clearing local state and redirecting
  // would claim the session had ended while the cookie stayed valid — and the
  // next syncSession would restore the live support session. So: confirm the
  // server invalidated it, and only then tear down locally.
  const handleEnd = async () => {
    setEnding(true);
    setEndError(null);
    try {
      // Stop the tutor runtime first, while the support cookie is still valid.
      // signOut removes the cookie, and the pagehide handler's own
      // stop_lesson POST would then arrive unauthenticated — the socket would
      // close but the cached Session, its queue worker and any in-flight
      // generation would survive until the 120-minute eviction job.
      try {
        await stopLessonSession();
      } catch {
        // Best-effort: a failed stop must not prevent ending the session.
      }

      const { error } = await authClient.signOut();
      if (error) {
        setEndError(
          "Could not end the session. The sign-in service did not respond. " +
            "Do not close this tab; try again.",
        );
        return;
      }
      await clearAuth({ skipServerSignOut: true });
      window.location.assign("/login");
    } catch {
      setEndError(
        "Could not end the session. The sign-in service is unreachable. " +
          "Do not close this tab; try again.",
      );
    } finally {
      setEnding(false);
    }
  };

  return (
    <div
      ref={barRef}
      role="status"
      aria-live="polite"
      className="sticky top-0 z-[9999] flex w-full flex-wrap items-center justify-between gap-2 bg-amber-500 px-4 py-2 text-sm text-black shadow-md"
    >
      <span className="font-medium">
        Support session. You are signed in as{" "}
        <span className="font-semibold">{who}</span>. Activity here is not
        recorded against their progress.
      </span>
      <div className="flex shrink-0 items-center gap-3">
        {endError ? (
          <span role="alert" className="text-xs font-semibold">
            {endError}
          </span>
        ) : null}
        <button
          type="button"
          onClick={handleEnd}
          disabled={ending}
          className="shrink-0 rounded-md bg-black/85 px-3 py-1 font-semibold text-white transition hover:bg-black disabled:opacity-60"
        >
          {ending ? "Ending…" : endError ? "Try again" : "End support session"}
        </button>
      </div>
    </div>
  );
};

export default SupportSessionBanner;
